//
// AHT Weight Management — Liraglutide (Saxenda®/Biolide®) shared screening
// downstream. Single source of truth for the post-confirmation steps shared by
// the initial supply and full re-screen pathways: PGD fresh checks, interaction
// checks, cautions review, supply attestation, and the shared outcomes.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { OutcomeNode, PathwayNode } from "../../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

export const PGD_FRESH_CHECKS = "lira-init-pgd-fresh-checks";
export const Q3_INT_GLP1      = "lira-init-int1-glp1";
export const Q4_INT_INS       = "lira-init-int2-insulin-sulph";
export const Q5_INT_WARF      = "lira-init-int3-warfarin";
export const Q6_INT_NTI       = "lira-init-int4-narrow-ti";
export const Q7_CAUTIONS      = "lira-init-cautions";
export const Q8_ATTEST        = "lira-init-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Outcome IDs
// ─────────────────────────────────────────────────────────────────────────────

export const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
export const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
export const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Shared downstream nodes — spread into a pathway's nodes array after the
// confirmation / exclusion-question stage. The first node is PGD_FRESH_CHECKS.
// ─────────────────────────────────────────────────────────────────────────────

export const liraglutideScreeningDownstreamNodes: PathwayNode[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // Step 2b — PGD inclusion / exclusion checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: PGD_FRESH_CHECKS,
    type: "pgd_exclusion_check",
    variant: "sequential",
    pgdIdOverride: "pgd-aht-wt-liraglutide",
    anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
    allCleared: Q3_INT_GLP1,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 3 — Interaction checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q3_INT_GLP1,
    type: "condition_route",
    conditions: [
      {
        evaluate: (ctx) => ctx.context["sharedConcurrentWeightAgent"] === true,
        then: { outcomeId: OUT_CANNOT_SUPPLY },
      },
    ],
    default: Q4_INT_INS,
  },

  {
    id: Q4_INT_INS,
    type: "yes_no",
    text: "Is the patient currently using insulin or sulphonylureas?",
    hint: "Increased hypoglycaemia risk in T2DM — review and consider dose reduction before initiation.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the glucose management plan and any anticipated insulin or sulphonylurea dose reduction",
      acknowledgementText: "Patient has been counselled on hypoglycaemia risk and understands the management plan",
    },
    yes: Q5_INT_WARF,
    no: Q5_INT_WARF,
    autoAnswer: { when: { hasMedication: ["insulin", "gliclazide", "glibenclamide", "glimepiride", "glipizide", "tolbutamide"] }, value: true },
  },

  {
    id: Q5_INT_WARF,
    type: "yes_no",
    text: "Is the patient taking warfarin or other coumarin anticoagulants?",
    hint: "Delayed gastric emptying may affect absorption — monitor INR more frequently after initiation.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document INR monitoring plan and frequency",
      acknowledgementText: "Patient and GP/anticoagulation clinic have been informed of enhanced INR monitoring",
    },
    yes: Q6_INT_NTI,
    no: Q6_INT_NTI,
    autoAnswer: { when: { hasMedication: ["warfarin", "acenocoumarol", "phenindione"] }, value: true },
  },

  {
    id: Q6_INT_NTI,
    type: "yes_no",
    text: "Is the patient taking any oral medicines with a narrow therapeutic index?",
    hint: "Delayed gastric emptying may affect absorption — monitor where clinically relevant.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the monitoring and management plan for this interaction",
      acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
    },
    yes: Q7_CAUTIONS,
    no: Q7_CAUTIONS,
    autoAnswer: { when: { hasMedication: ["ciclosporin", "digoxin", "lithium", "phenytoin", "theophylline", "warfarin"] }, value: true },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 4 — Cautions review
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q7_CAUTIONS,
    type: "flag",
    variant: "warn",
    message:
      "Step 4 — Review cautions before supply:\n" +
      "• Pancreatitis: advise patients to report persistent severe abdominal pain; discontinue if suspected; do not restart if confirmed\n" +
      "• Heart rate: mean increase ~2.5 bpm; monitor resting HR; discontinue if clinically significant sustained increase\n" +
      "• Cholelithiasis/cholecystitis: higher rate than placebo; advise right upper quadrant pain, nausea, fever → refer\n" +
      "• Dehydration and acute renal failure with GI effects — monitor CKD patients; encourage hydration\n" +
      "• Not a substitute for insulin — do not discontinue insulin without prescriber advice\n" +
      "• Aspiration risk — advise anaesthetists/surgical team of liraglutide use before GA or sedation\n" +
      "• Rotate injection sites every injection — risk of cutaneous amyloidosis\n" +
      "• Adolescents: vomiting ~2× more frequent; monitor for disordered eating and psychological distress at every review",
    next: Q8_ATTEST,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 5 — Supply attestation
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q8_ATTEST,
    type: "attestation",
    text: "Step 5 — Confirm supply of Saxenda®/Biolide® 6mg/mL pre-filled pen",
    attestationStatement:
      "I confirm that I have:\n" +
      "• Supplied 1 × Saxenda®/Biolide® 6mg/mL 3mL pre-filled pen\n" +
      "• Recorded BRAND NAME (Saxenda® or Biolide®) AND batch number (biological product — required at every supply)\n" +
      "• Checked cold chain integrity; pen must not have been frozen\n" +
      "• Checked solution is clear and colourless\n" +
      "• Supplied compatible needles separately (not included in pen)\n" +
      "• Instructed on injection technique and site rotation\n" +
      "• For adolescents: parent/guardian also understands technique and schedule\n" +
      "• Advised patient to inform GP on initiation\n" +
      "• Pancreatitis warning: severe abdominal pain → stop, attend A&E\n" +
      "• Provided patient information leaflet",
    conclusions: {
      optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
      optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared outcomes — used by both the initial supply and re-screen pathways.
// ─────────────────────────────────────────────────────────────────────────────

export const liraglutideScreeningOutcomes: Record<string, OutcomeNode> = {
  [OUT_NO_F2F]: {
    id: OUT_NO_F2F,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — face-to-face consultation required",
    reason:
      "Face-to-face consultation is required for initial supply to adults and for all supply to adolescents under this PGD.",
    safetyNetting: [
      "Ask the patient to book an in-person appointment to proceed with supply.",
    ],
  },
  [OUT_CANNOT_SUPPLY]: {
    id: OUT_CANNOT_SUPPLY,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — Saxenda®/Biolide® (liraglutide)",
    reason:
      "An inclusion criterion was not met or an exclusion criterion was triggered. Supply cannot proceed under this PGD.",
    safetyNetting: [
      "Discuss the reason supply cannot proceed with the patient.",
      "Consider referral to prescriber or GP if clinically appropriate.",
    ],
  },
  [OUT_SUPPLY]: {
    id: OUT_SUPPLY,
    type: "outcome",
    outcomeType: "supply",
    title: "Supply complete — liraglutide 0.6mg (initial supply)",
    reason:
      "All inclusion criteria met, no exclusion criteria triggered, and supply attestation confirmed.",
    drugRef: "liraglutide",
    safetyNetting: [
      "Return in 4 weeks for monitoring review.",
      "Missed dose: if within 12 hours of usual time — take now. If less than 12 hours to next dose — skip.",
      "Pancreatitis warning: severe abdominal pain → stop injecting, attend A&E.",
      "Gallstones: right upper quadrant pain, nausea, fever → seek advice.",
      "Tell anaesthetist/surgical team you are taking liraglutide before any GA or sedation.",
      "12-week review: adults <5% weight loss on 3.0mg → discontinuation. Adolescents <4% BMI/z-score reduction → discontinuation.",
    ],
  },
};
